/**
 * HelpModal — support request modal with auto-capture, paste, and upload.
 *
 * Screenshot modes (mutually exclusive, in priority order):
 *   1. Auto-capture via html2canvas on open (background, non-blocking)
 *   2. Paste — Cmd+V / Ctrl+V into the paste zone replaces auto
 *   3. Upload — file picker, image/* only, max 5 MB
 *
 * The active screenshot (whichever is current) is sent as screenshot_data_url.
 * If the user clicks Remove and nothing replaces it, screenshot_data_url is null.
 *
 * Sub28 (2026-05-14) — three polishes:
 *   1. Urgency override — keyword classifier still runs, but the user can
 *      bump or drop the tier via a three-pill control. The classifier value
 *      seeds the initial selection and re-seeds when the subject changes
 *      (only while the user has not manually overridden).
 *   2. Tenant id is captured from the session and posted alongside user
 *      context. Server already enforces tenant from JWT; this is metadata
 *      for the receiving inbox.
 *   3. Inline success card replaces the toast after submit, so the user can
 *      see the ticket id, expected response window, and a Close button at
 *      their own pace instead of having a toast disappear in 3 seconds.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { api, getSession } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Auto-classify a ticket into one of three tiers based on subject keywords.
 * Used by the 11am cron Slack DM to group tickets by urgency type.
 *   tier 1 — bugs, login, score (default)
 *   tier 2 — pricing, billing, account
 *   tier 3 — security, churn, angry-enterprise
 *
 * Match order: tier 3 first (most severe), then tier 2, else tier 1 fallback.
 */
export function classifyTier(subject: string): 1 | 2 | 3 {
  const s = subject.toLowerCase();
  const tier3 = ["security", "breach", "cancel", "churn", "angry", "enterprise", "lawsuit", "legal"];
  const tier2 = ["price", "pricing", "billing", "invoice", "account", "payment", "refund", "subscription"];
  const tier1 = ["bug", "error", "broken", "login", "score", "crash", "500", "404"];
  if (tier3.some((k) => s.includes(k))) return 3;
  if (tier2.some((k) => s.includes(k))) return 2;
  if (tier1.some((k) => s.includes(k))) return 1;
  return 1;
}

/** Downscale a data URL to stay under MAX_BYTES. */
async function downscaleDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      let quality = 0.85;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(dataUrl); return; }

      const attempt = (w: number, h: number, q: number): string => {
        canvas.width = w;
        canvas.height = h;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        return canvas.toDataURL("image/jpeg", q);
      };

      let result = attempt(width, height, quality);
      while (result.length * 0.75 > MAX_BYTES && width > 200) {
        width = Math.floor(width * 0.75);
        height = Math.floor(height * 0.75);
        quality = Math.max(0.5, quality - 0.05);
        result = attempt(width, height, quality);
      }
      resolve(result);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

async function captureScreen(): Promise<string | null> {
  try {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 2),
    });
    let dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    if (dataUrl.length * 0.75 > MAX_BYTES) {
      dataUrl = await downscaleDataUrl(dataUrl);
    }
    return dataUrl;
  } catch {
    return null;
  }
}

function fileToDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      let dataUrl = ev.target?.result as string;
      if (dataUrl.length * 0.75 > MAX_BYTES) {
        dataUrl = await downscaleDataUrl(dataUrl);
      }
      resolve(dataUrl);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

type TierChoice = 1 | 2 | 3;

const TIER_PILLS: Array<{ value: TierChoice; label: string; hint: string }> = [
  { value: 1, label: "Routine",  hint: "Reply within one business day" },
  { value: 2, label: "Important", hint: "Reply within four hours during business hours" },
  { value: 3, label: "Urgent",    hint: "Bryan paged immediately — use sparingly" },
];

export function HelpModal({ open, onClose }: HelpModalProps) {
  const [loc] = useLocation();
  const { toast } = useToast();

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [page, setPage] = useState(loc);

  // Tier: classifier seeds the value; once the user clicks a pill, tierTouched
  // becomes true and we stop re-seeding from subject changes.
  const [tier, setTier] = useState<TierChoice>(1);
  const [tierTouched, setTierTouched] = useState(false);

  const [screenshot, setScreenshot] = useState<string | null | undefined>(undefined);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ id: string | number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pasteZoneRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  // Reset on open + kick off auto-capture
  useEffect(() => {
    if (!open) return;

    setPage(loc);
    setSubject("");
    setDescription("");
    setTier(1);
    setTierTouched(false);
    setScreenshot(undefined);
    setScreenshotError(null);
    setSubmitError(null);
    setSubmitted(null);
    setTimeout(() => subjectRef.current?.focus(), 50);

    captureScreen().then((dataUrl) => {
      setScreenshot((prev) => (prev === undefined ? (dataUrl ?? null) : prev));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-seed tier from subject changes only while the user has not overridden.
  useEffect(() => {
    if (tierTouched) return;
    setTier(classifyTier(subject));
  }, [subject, tierTouched]);

  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        if (!blob) continue;
        if (blob.size > MAX_BYTES * 1.4) {
          setScreenshotError("Pasted image exceeds 5 MB. Try a smaller one.");
          return;
        }
        setScreenshotError(null);
        const dataUrl = await fileToDataUrl(blob);
        setScreenshot(dataUrl);
        return;
      }
    }
  }, []);

  // Esc dismissal
  useEffect(() => {
    if (!open) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // Global paste when modal is open
  useEffect(() => {
    if (!open) return;
    const handleGlobalPaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const blob = item.getAsFile();
          if (!blob) continue;
          setScreenshotError(null);
          const dataUrl = await fileToDataUrl(blob);
          setScreenshot(dataUrl);
          return;
        }
      }
    };
    document.addEventListener("paste", handleGlobalPaste);
    return () => document.removeEventListener("paste", handleGlobalPaste);
  }, [open]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setScreenshotError("Only image files are accepted.");
      return;
    }
    if (file.size > MAX_BYTES * 1.4) {
      setScreenshotError("File must be under 5 MB.");
      return;
    }
    setScreenshotError(null);
    const dataUrl = await fileToDataUrl(file);
    setScreenshot(dataUrl);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (description.trim().length < 20) {
      setSubmitError("Description must be at least 20 characters.");
      return;
    }
    setSending(true);
    setSubmitError(null);
    try {
      const sess = getSession();
      const body: Record<string, any> = {
        subject: subject.trim(),
        description: description.trim(),
        page: page.trim(),
        tier,
        // Metadata for the receiving inbox. Server enforces tenant from JWT
        // — these fields are informational and do not bypass RLS.
        tenant_id: sess?.tenantId ?? null,
        user_email: sess?.email ?? null,
      };
      if (typeof screenshot === "string") {
        body.screenshot_data_url = screenshot;
      }

      const res = await api("/api/support-tickets", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const ticketId =
        (res && typeof res === "object" && (res as any).id) ||
        (res && typeof res === "object" && (res as any).ticket_id) ||
        "";

      setSubmitted({ id: ticketId });
      // Keep the toast for users who close the modal immediately
      toast({
        title: "Ticket received. Our support team will be in touch shortly.",
      });
    } catch (err: any) {
      setSubmitError(err?.message || "Could not submit. Try again.");
    } finally {
      setSending(false);
    }
  }

  const session = getSession();

  function buildMailtoBody() {
    const name = session?.name ?? session?.email ?? "Unknown user";
    const email = session?.email ?? "";
    return encodeURIComponent(
      `Page: ${window.location.href}\nUser: ${name} (${email})\n\n`,
    );
  }

  if (!open) return null;

  const capturing = screenshot === undefined;
  const hasScreenshot = typeof screenshot === "string";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Get help"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="relative w-full max-w-lg rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[hsl(var(--border))] flex-shrink-0">
          <h2 className="text-sm font-semibold text-foreground tracking-tight">
            {submitted ? "Ticket received" : "Get help"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close help modal"
            className="text-[hsl(var(--muted-foreground))] hover:text-foreground transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        {submitted ? (
          // ── Success card (Sub28) ──────────────────────────────────────
          <div className="px-6 py-6 overflow-y-auto flex-1">
            <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-5 space-y-3">
              <p className="text-sm text-foreground">
                Thanks. Your ticket is in the queue.
              </p>
              {submitted.id ? (
                <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
                  Reference:{" "}
                  <span className="font-mono text-foreground">#{submitted.id}</span>
                </p>
              ) : null}
              <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
                {tier === 3
                  ? "Bryan is paged. Expect a reply within minutes."
                  : tier === 2
                  ? "A member of the team will reply within four business hours."
                  : "A member of the team will reply within one business day."}
              </p>
              <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
                Watch for an email from
                {" "}<span className="font-mono">support_team@mi6group.com</span>.
              </p>
            </div>
            <div className="pt-4">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-4 py-2 rounded bg-[hsl(var(--accent-red))] text-white text-[13px] font-medium hover:opacity-90 transition-opacity"
                data-testid="help-close-after-submit"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 overflow-y-auto flex-1">
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] mb-4">
              Tell us what's going on. Our support team will jump on it.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="hs-subject" className="block text-[11px] font-medium text-[hsl(var(--muted-foreground))] mb-1">
                  Subject <span className="text-[hsl(var(--accent-red))]">*</span>
                </label>
                <input
                  id="hs-subject"
                  ref={subjectRef}
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Brief summary of the issue"
                  className="w-full px-3 py-2 rounded border border-[hsl(var(--border))] bg-background
                    text-sm text-foreground placeholder:text-muted-foreground
                    focus:outline-none focus:border-[hsl(var(--accent-red))] transition-colors"
                />
              </div>

              <div>
                <label htmlFor="hs-description" className="block text-[11px] font-medium text-[hsl(var(--muted-foreground))] mb-1">
                  Description <span className="text-[hsl(var(--accent-red))]">*</span>
                </label>
                <textarea
                  id="hs-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={4}
                  placeholder="What were you trying to do? What happened instead? (min 20 characters)"
                  className="w-full px-3 py-2.5 rounded border border-[hsl(var(--border))] bg-background
                    text-sm text-foreground placeholder:text-muted-foreground resize-none
                    focus:outline-none focus:border-[hsl(var(--accent-red))] transition-colors"
                />
                {description.length > 0 && description.length < 20 && (
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                    {20 - description.length} more character{20 - description.length !== 1 ? "s" : ""} needed
                  </p>
                )}
              </div>

              {/* Urgency override (Sub28) */}
              <div>
                <span
                  id="hs-urgency-label"
                  className="block text-[11px] font-medium text-[hsl(var(--muted-foreground))] mb-1"
                >
                  Urgency
                </span>
                <div
                  role="radiogroup"
                  aria-labelledby="hs-urgency-label"
                  className="flex gap-2"
                >
                  {TIER_PILLS.map((pill) => {
                    const active = tier === pill.value;
                    return (
                      <button
                        key={pill.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => {
                          setTier(pill.value);
                          setTierTouched(true);
                        }}
                        title={pill.hint}
                        className={
                          "flex-1 px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors " +
                          (active
                            ? "bg-[hsl(var(--accent-red))] text-white border-[hsl(var(--accent-red))]"
                            : "bg-background text-foreground border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]")
                        }
                      >
                        {pill.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                  {TIER_PILLS.find((p) => p.value === tier)?.hint}
                </p>
              </div>

              <div>
                <label htmlFor="hs-page" className="block text-[11px] font-medium text-[hsl(var(--muted-foreground))] mb-1">
                  Page where issue occurred
                </label>
                <input
                  id="hs-page"
                  type="text"
                  value={page}
                  onChange={(e) => setPage(e.target.value)}
                  maxLength={1000}
                  className="w-full px-3 py-2 rounded border border-[hsl(var(--border))] bg-background
                    text-sm text-foreground placeholder:text-muted-foreground
                    focus:outline-none focus:border-[hsl(var(--accent-red))] transition-colors font-mono text-[12px]"
                />
              </div>

              <div className="space-y-2">
                <span className="block text-[11px] font-medium text-[hsl(var(--muted-foreground))]">
                  Screenshot
                </span>

                {capturing ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--border))] bg-background">
                    <span className="text-[11px] text-muted-foreground animate-pulse">
                      Capturing screenshot...
                    </span>
                  </div>
                ) : hasScreenshot ? (
                  <div className="rounded border border-[hsl(var(--border))] bg-background overflow-hidden">
                    <img
                      src={screenshot}
                      alt="Screenshot preview"
                      className="w-full max-h-36 object-cover object-top"
                    />
                    <div className="flex items-center justify-between px-3 py-1.5 border-t border-[hsl(var(--border))]">
                      <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                        Screenshot of what you're seeing
                        <span className="text-muted-foreground opacity-70"> (sent automatically — click Remove to skip)</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-foreground transition-colors ml-3 flex-shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground px-3 py-2 rounded border border-[hsl(var(--border))] bg-background">
                    No screenshot attached.
                  </div>
                )}

                <div
                  ref={pasteZoneRef}
                  onPaste={handlePaste}
                  tabIndex={0}
                  role="textbox"
                  aria-label="Paste a screenshot here"
                  className="px-3 py-2.5 rounded border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--surface-2))]
                    text-[11px] text-muted-foreground text-center cursor-text
                    focus:outline-none focus:border-[hsl(var(--muted-foreground))] transition-colors
                    hover:border-[hsl(var(--muted-foreground))] hover:text-foreground"
                >
                  Paste a different screenshot here (Cmd+V / Ctrl+V)
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-foreground transition-colors underline underline-offset-2"
                  >
                    Or upload an image
                  </button>
                  <span className="text-[11px] text-muted-foreground ml-1">(image only, max 5 MB)</span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  aria-hidden="true"
                />

                {screenshotError && (
                  <p className="text-[11px] text-rose-600">{screenshotError}</p>
                )}
              </div>

              {submitError && (
                <p className="text-[12px] text-rose-600">{submitError}</p>
              )}

              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="submit"
                  disabled={sending || subject.trim().length === 0 || description.trim().length < 20}
                  className="w-full px-4 py-2 rounded bg-[hsl(var(--accent-red))] text-white text-[13px] font-medium
                    hover:opacity-90 disabled:opacity-50 transition-opacity"
                  data-testid="help-send"
                >
                  {sending ? "Sending..." : "Send"}
                </button>

                <a
                  href={`mailto:support@mi6group.com?subject=AIP%20support&body=${buildMailtoBody()}`}
                  className="text-center text-[12px] text-[hsl(var(--muted-foreground))] hover:text-foreground transition-colors py-1"
                >
                  Send by email instead
                </a>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
